import React, { useEffect, useRef, useState } from 'react';
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";


const DriveEasyEyePlayer = ({ deviceId = "AICAM1" }) => {

  const videoRef = useRef(null);
  const [status, setStatus] = useState('connecting');
  const [detections, setDetections] = useState(null);


  const SIGNALING_SERVER =
    "wss://driveeasy-eye-signaling-904892438797.us-central1.run.app";

  useEffect(() => {

    let websocket;
    let peerConnection;

    const initWebRTC = async () => {

      websocket = new WebSocket(SIGNALING_SERVER);

      websocket.onopen = () => {

        console.log("Connected to signaling server");

        setStatus("registering");

        websocket.send(JSON.stringify({
          type: "register-viewer",
          deviceId: deviceId
        }));

      };

      websocket.onmessage = async (event) => {

        try {

          let text;

          if (typeof event.data === "string")
            text = event.data;
          else
            text = await event.data.text();

          const data = JSON.parse(text);

          console.log("Received:", data.type);

          switch (data.type) {

            case "registered":

              setStatus("waiting");
              console.log("Waiting for camera...");
              break;


            case "offer":

              console.log("Offer received");

              setStatus("connecting");

              peerConnection = new RTCPeerConnection({
                iceServers: [
                  { urls: "stun:stun.l.google.com:19302" },
                  { urls: "stun:stun1.l.google.com:19302" }
                ]
              });

              peerConnection.ontrack = (event) => {

                console.log("Video track received");

                videoRef.current.srcObject = event.streams[0];

                setStatus("streaming");

              };

              peerConnection.onicecandidate = (event) => {

                if (event.candidate) {

                  websocket.send(JSON.stringify({
                    type: "ice-candidate",
                    deviceId: deviceId,
                    candidate: event.candidate
                  }));

                }

              };

              await peerConnection.setRemoteDescription(
                new RTCSessionDescription(data.sdp)
              );

              const answer =
                await peerConnection.createAnswer();

              await peerConnection.setLocalDescription(answer);

              websocket.send(JSON.stringify({
                type: "answer",
                deviceId: deviceId,
                sdp: answer
              }));

              console.log("Answer sent");

              break;


            case "ice-candidate":

              if (peerConnection) {

                await peerConnection.addIceCandidate(
                  new RTCIceCandidate(data.candidate)
                );

              }

              break;

          }

        } catch (err) {

          console.error("Message error:", err);

        }

      };

      websocket.onerror = (err) => {

        console.error("WebSocket error", err);

        setStatus("error");

      };

      websocket.onclose = () => {

        console.log("WebSocket closed");

        setStatus("disconnected");

      };

    };

    initWebRTC();

    return () => {

      if (websocket)
        websocket.close();

      if (peerConnection)
        peerConnection.close();

    };

  }, [deviceId]);

  useEffect(() => {

    const ref =
      doc(db, "driveeasy_eye_live", deviceId);

    const unsub = onSnapshot(ref, (snap) => {

      if (snap.exists()) {

        setDetections(snap.data());

      }

    });

    return () => unsub();

  }, [deviceId]);


  const getStatusColor = () => {

    switch (status) {

      case "streaming":
        return "lime";

      case "waiting":
      case "connecting":
      case "registering":
        return "yellow";

      case "error":
      case "disconnected":
        return "red";

      default:
        return "gray";

    }

  };


  const getStatusText = () => {

    switch (status) {

      case "streaming":
        return "LIVE";

      case "waiting":
        return "Waiting for camera...";

      case "connecting":
        return "Connecting...";

      case "registering":
        return "Registering...";

      case "error":
        return "Connection Error";

      case "disconnected":
        return "Disconnected";

      default:
        return "Unknown";

    }

  };


  return (

    <div style={{
      width: "100vw",
      height: "100vh",
      margin: "0",
      position: "relative",
      background: "black",
      overflow: "hidden"
    }}>


      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover"
        }}
      />


      {/* Premium HUD Overlay */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        padding: "20px",
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
        gap: "20px"
      }}>

        {/* Top Section: Status & Title */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          width: "100%"
        }}>
          <div style={{
            background: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(12px)",
            padding: "8px 16px",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}>
            <div style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              background: getStatusColor(),
              boxShadow: `0 0 10px ${getStatusColor()}`,
              animation: status === "streaming" ? "pulse 2s infinite" : "none"
            }} />
            <span style={{
              color: "white",
              fontWeight: "600",
              fontSize: "14px",
              letterSpacing: "0.5px"
            }}>
              {getStatusText()}
            </span>
            <span style={{ color: "rgba(255,255,255,0.4)", margin: "0 4px" }}>|</span>
            <span style={{ color: "white", opacity: 0.8, fontSize: "13px" }}>{deviceId}</span>
          </div>

          <style>{`
            @keyframes pulse {
              0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.7); }
              70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(0, 255, 0, 0); }
              100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(0, 255, 0, 0); }
            }
          `}</style>
        </div>

        {/* Content Section: Detections & Data */}
        {detections && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: "15px",
            maxWidth: "120px"
          }}>

            {/* Device Info Card */}
            <div style={{
              background: "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(16px)",
              padding: "15px",
              borderRadius: "16px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              display: "flex",
              flexDirection: "column",
              gap: "5px"
            }}>
              <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase" }}>Telemetry</span>
              <div style={{ color: "white", fontSize: "12px", opacity: 0.8 }}>
                Last Sync: {detections.deviceTimestamp ? new Date(detections.deviceTimestamp).toLocaleTimeString() : "N/A"}
              </div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "10px" }}>
                {detections.deviceTimestamp ? new Date(detections.deviceTimestamp).toLocaleDateString() : ""}
              </div>
            </div>

          </div>
        )}
      </div>

    </div>

  );



};

export default DriveEasyEyePlayer;
